import { useEffect, useRef } from "react";
import { FileText, Image as ImageIcon, Mic, Loader2 } from "lucide-react";
import { useAttachmentBlob, drawsFromItsBytes } from "../hooks/useAttachmentBlob";

// A INVARIANTE DO MODULO. O destino (<img src>, <audio src>, <a download>) nao
// e o guarda: um blob: herda a origem de quem o mintou, que e o backoffice, e
// o menu de contexto do browser abre "Abrir imagem numa nova aba" ou "Abrir
// link numa nova aba" sobre esse blob sem passar pelo `download`, que so vale
// no clique esquerdo direto. Se um documento chegasse a ser desenhado, esse
// menu punha um documento com script na origem onde vivem access_token e
// refresh_token (tokenStorage.js, em localStorage). O bearer protege a ROTA,
// nao o que o browser faz depois de os bytes ja estarem em memoria.
//
// O guarda de verdade e dois: nunca mintar um blob desenhavel para um tipo
// que possa ser um documento (drawsFromItsBytes, no hook, e quem decide isso:
// so audio e um raster allowlist, nunca image/svg+xml), e o blob do download
// ser sempre re-tipado para application/octet-stream, para que mesmo uma
// navegacao direta a ele descarregue em vez de desenhar (useAttachmentBlob
// trata disso). accepted_mime no backend e uma allowlist pura sem denylist,
// portanto isto fica vivo no dia em que um tenant meter image/svg+xml na
// config, nao so quando o tipo e claramente um documento.

// O tamanho legivel de um anexo, so para os tipos em que ele diz alguma
// coisa a quem le. Mesmo formato de FileViewer.js formatFileSize.
const formatAttachmentSize = (bytes) => {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// O content_type diz o que o cliente mandou. Nos audios o tamanho em bytes
// nao significa nada para quem le, seria a duracao a dizer alguma coisa, e
// essa nao esta no payload.
const attachmentKind = (contentType = "") => {
  if (contentType.startsWith("image/")) return { Icon: ImageIcon, key: "conversationsKindImage", withSize: true };
  if (contentType.startsWith("audio/")) return { Icon: Mic, key: "conversationsKindVoice", withSize: false };
  return { Icon: FileText, key: "conversationsKindDocument", withSize: true };
};

// A moldura e o corpo comuns aos varios estados que o chip atravessa: parado,
// a carregar, recusado e, so no pdf ou tipo desconhecido, pronto a
// descarregar. So muda o elemento por baixo (`as`, um button, um a ou um
// div), o icone e o texto da primeira linha. O filename, quando existe, so
// aparece aqui, na segunda linha, para nao se repetir como texto de uma
// ancora que tambem o levasse.
const ChipShell = ({ as: As = "div", icon: Icon, iconClassName = "", label, filename, ...rest }) => (
  <As
    className="inline-flex items-start gap-2 max-w-full px-3 py-2 rounded-lg border border-[var(--color-border)] text-left"
    style={{ backgroundColor: "var(--color-surface)" }}
    {...rest}
  >
    <Icon
      className={`w-4 h-4 shrink-0 mt-0.5 ${iconClassName}`}
      style={{ color: "var(--color-text-secondary)" }}
      aria-hidden="true"
    />
    <div className="min-w-0">
      <p className="text-[13px] leading-[18px]" style={{ color: "var(--color-text-primary)" }}>
        {label}
      </p>
      {filename && (
        <p className="text-xs truncate" style={{ color: "var(--color-text-secondary)" }} title={filename}>
          {filename}
        </p>
      )}
    </div>
  </As>
);

const SizeSuffix = ({ size }) =>
  size ? <span style={{ color: "var(--color-text-secondary)" }}>{` ${size}`}</span> : null;

// Nota de voz: <audio controls> nativo, decidido a 2026-09-08. Sem leitor a
// mao, sem waveform sintetizada de uma semente, que nao diz nada de
// verdadeiro sobre a gravacao. O nativo da scrubbing, velocidade, volume e
// teclado de graca, e nao precisa de Range porque o blob esta inteiro em
// memoria.
// O MediaRecorder do Chrome escreve o webm da nota de voz sem elemento
// Duration no container. Isso faz `audio.duration` chegar a `Infinity` assim
// que os metadados carregam, e o controlo nativo entao so mostra o tempo
// decorrido (`0:01`), sem o total e com a barra truncada, ainda que a
// reproducao e o arrasto continuem a funcionar. Um ficheiro que ja traga
// duracao no container fica de fora disto, o hook so mexe quando
// Number.isFinite(audio.duration) e falso.
//
// Medido no Chrome, neste exato ficheiro: um seek para um tempo absurdo
// (1e101, muito alem do fim) forca o browser a percorrer o ficheiro e
// recalcular a duracao real. `duration` passou de `Infinity` a `4.001`
// (o comprimento verdadeiro da nota) em 29ms; o cursor andou ate ao fim e
// voltou ao 0 sem ruido nenhum, o elemento ficou "paused" o tempo todo, e os
// unicos eventos disparados foram durationchange, timeupdate e seeked, nunca
// play nem ended. E o `<audio controls>` nativo a aprender a propria
// duracao, nao um leitor escrito a mao: mantemos a decisao de 2026-09-08 de
// nao reimplementar scrubbing, velocidade, volume e teclado.
//
// O widget tem o mesmo problema e resolve-o de outra forma
// (attachment-preview-list.tsx): calcula a duracao no proprio codigo e so a
// aceita quando Number.isFinite(duration) e verdadeiro, sem tocar em
// currentTime. Aqui o ficheiro inteiro ja esta em memoria como blob e o
// elemento e nativo, por isso o mais simples e ensinar ao proprio <audio>
// a sua duracao.
function useFixInfiniteWebmDuration(audioRef) {
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;

    // So corre uma vez por elemento. Pos currentTime a mexer dispara
    // timeupdate, e um handler ingenuo que voltasse a verificar
    // Number.isFinite e a fazer seek outra vez entrava num ciclo. Esta flag
    // fecha a porta assim que o truque comeca.
    let started = false;
    let onTimeUpdate = null;

    const onLoadedMetadata = () => {
      if (started) return;
      if (Number.isFinite(el.duration)) return; // ja tem duracao real, nao mexer
      if (!el.paused || el.currentTime !== 0) return; // o operador ja tocou ou arrastou, nao lutar contra isso
      started = true;

      onTimeUpdate = () => {
        el.removeEventListener("timeupdate", onTimeUpdate);
        el.currentTime = 0; // repor o inicio depois de o browser aprender a duracao real
      };
      el.addEventListener("timeupdate", onTimeUpdate);
      el.currentTime = 1e101; // fora do ficheiro de proposito, e o que forca o recalculo
    };

    el.addEventListener("loadedmetadata", onLoadedMetadata);
    // O efeito corre depois da pintura, o evento pode ja ter passado: se os
    // metadados ja estiverem prontos (readyState >= 1) o listener sozinho
    // nunca ia disparar, e a duracao ficava presa em Infinity.
    if (el.readyState >= 1) onLoadedMetadata();
    return () => {
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      if (onTimeUpdate) el.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [audioRef]);
}

const AudioAttachment = ({ url, filename, kindLabel }) => {
  const audioRef = useRef(null);
  useFixInfiniteWebmDuration(audioRef);
  return (
    <audio
      ref={audioRef}
      // Mantido de proposito: este repo verifica-se no browser e nao em
      // testes, e este e o seletor estavel dessa verificacao, nao codigo morto.
      data-testid="attachment-audio"
      controls
      src={url}
      title={filename}
      aria-label={filename || kindLabel}
    />
  );
};

// alt="", vazio, e decorativo para um leitor de ecra, que o salta por
// completo. Um filename vazio existe, por exemplo numa imagem colada da
// area de transferencia, e nesse caso o alt cai para o rotulo do tipo
// (o mesmo texto que o chip diria), que e menos do que o nome mas ainda
// anuncia que ali houve conteudo do cliente.
const ImageAttachment = ({ url, filename, kindLabel }) => (
  <img src={url} alt={filename || kindLabel} className="max-w-[240px] max-h-[240px] rounded-lg block" />
);

// Usa o hook que busca os bytes do anexo e devolve o blob: URL pronto a
// desenhar. Imagem e audio pedem os bytes ao montar (o hook decide isso por
// drawsFromItsBytes); um pdf ou um tipo desconhecido so pede quando o
// operador clica no chip, atraves de `request()`, e o chip parado e um
// <button>, o unico botao do componente.
const AttachmentBlock = ({ payload, conversationId, translate }) => {
  const { url, failed, loading, request } = useAttachmentBlob({
    conversationId,
    attachmentId: payload.attachment_id,
    contentType: payload.content_type,
  });
  const { Icon, key, withSize } = attachmentKind(payload.content_type);
  const size = withSize ? formatAttachmentSize(payload.size_bytes) : null;
  const inline = drawsFromItsBytes(payload.content_type);

  if (failed) {
    return (
      <ChipShell
        as="button"
        type="button"
        onClick={request}
        icon={Icon}
        filename={payload.filename}
        label={translate("conversationsAttachmentFailed")}
      />
    );
  }

  if (inline) {
    if (!url) {
      return (
        <ChipShell
          role="status"
          icon={Loader2}
          iconClassName="animate-spin"
          filename={payload.filename}
          label={translate("conversationsAttachmentLoading")}
        />
      );
    }
    return payload.content_type.startsWith("audio/") ? (
      <AudioAttachment url={url} filename={payload.filename} kindLabel={translate(key)} />
    ) : (
      <ImageAttachment url={url} filename={payload.filename} kindLabel={translate(key)} />
    );
  }

  if (url) {
    return (
      <ChipShell
        as="a"
        href={url}
        download={payload.filename || true}
        icon={Icon}
        filename={payload.filename}
        label={
          <>
            {translate("conversationsAttachmentDownload")}
            <SizeSuffix size={size} />
          </>
        }
      />
    );
  }

  if (loading) {
    return (
      <ChipShell
        role="status"
        icon={Loader2}
        iconClassName="animate-spin"
        filename={payload.filename}
        label={translate("conversationsAttachmentLoading")}
      />
    );
  }

  return (
    <ChipShell
      as="button"
      type="button"
      onClick={request}
      icon={Icon}
      filename={payload.filename}
      label={
        <>
          {translate(key)}
          <SizeSuffix size={size} />
        </>
      }
    />
  );
};

export default AttachmentBlock;
